import type { Ref } from 'react'
import { useState, useMemo } from 'react'
import { Spacer } from '@truepill/react-capsule'
import { isRefillTooSoonReason } from '@truepill/tpos-data-util'
import type { TriageReasons, TriageParams, OrderStatus, BulkTriageEntry } from '@truepill/tpos-types'
import { ReactComponent as DangerIcon } from 'assets/icons/danger.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import { SaveButton } from 'components/ActionButton'
import IconWrapper from 'components/IconWrapper'
import { ModalHeader, ModalWrapper } from 'components/Modal'
import { ActionButton, CancelButton } from 'components/PageStructure'
import { StyledSelect } from 'components/RXTable'
import StyledDatePicker from 'components/StyledDatePicker'
import Warning from 'components/Warning'
import { TextArea, Box } from 'grommet'
import useAutoFocus from 'hooks/useAutoFocus'
import moment from 'moment'
import { useModalContext } from 'providers/Overlays/ModalProvider'
import { update } from 'ramda'
import styled from 'styled-components'
import { bodyPrimaryColor, borderColor, primaryBackgroundColor } from 'styles/styleVariables'
import { getBulkTriageReasons } from 'utils'

interface TriageEntryFieldsProps {
  index: number
  value: BulkTriageEntry
  triageReasons: string[]
  onChange: (entry: BulkTriageEntry, index: number) => void
}

const TriageEntryFields = ({ index, value, triageReasons, onChange }: TriageEntryFieldsProps): JSX.Element => {
  const autoFocusRef = useAutoFocus()
  const isRefillTooSoon = isRefillTooSoonReason(value.selectedReason)

  return (
    <InputContainer data-index={index}>
      {index > 0 && <TriageIndexHeader>Triage reason {index + 1}</TriageIndexHeader>}
      <ReasonSelect
        data-testid={`bulk-triage-reason-${index}`}
        ref={autoFocusRef as Ref<HTMLButtonElement>}
        multiple={false}
        maximumHeight="12rem"
        modal={true}
        value={value.selectedReason}
        placeholder="Select a triage reason..."
        options={triageReasons}
        onChange={([option]: { value: string }[]) => {
          const selectedReason = option ? option.value : ''
          onChange({ ...value, reason: selectedReason, selectedReason }, index)
        }}
      />
      {isRefillTooSoon && (
        <StyledDatePicker
          date={value.nextFillableDate ? moment(value.nextFillableDate, 'MM/DD/YYYY').toDate() : undefined}
          onChange={date => {
            const dateStr = moment(date).format('MM/DD/YYYY')
            onChange(
              {
                ...value,
                nextFillableDate: dateStr,
                inTriageUserMessage: `Refill available from ${dateStr}`,
              },
              index,
            )
          }}
          showPlaceholder={value.nextFillableDate === undefined}
        />
      )}
      <Spacer desktop="xl" />
      <Warning text="Any message will display to patients and external customers" />
      <TextArea
        data-testid={`bulk-triage-message-${index}`}
        onChange={event => onChange({ ...value, inTriageUserMessage: event.target.value }, index)}
        placeholder="Type a message..."
        value={value.inTriageUserMessage}
        resize="vertical"
      />
    </InputContainer>
  )
}

type BulkTriageModalProps = {
  onConfirm: (params: TriageParams) => void
  initialParams?: TriageParams
  orderStatus?: OrderStatus
  hasOtc?: boolean
}

const buildTriageParams = (triages: BulkTriageEntry[]): TriageParams => ({
  entries: triages.map(({ nextFillableDate, ...entry }) => {
    if (!nextFillableDate) return entry

    return {
      ...entry,
      details: {
        dateOkToFill: moment(nextFillableDate, 'MM/DD/YYYY').toDate(),
      },
    }
  }),
})

const BulkTriageModal = ({
  onConfirm,
  initialParams,
  orderStatus,
  hasOtc = false,
}: BulkTriageModalProps): JSX.Element => {
  const { dismissModal } = useModalContext()
  const [triages, setTriages] = useState<BulkTriageEntry[]>(initialParams?.entries ?? [{ reason: '' as TriageReasons }])
  const triageReasons = useMemo(() => getBulkTriageReasons(orderStatus, hasOtc), [orderStatus, hasOtc])

  const handleTriageUpdate = (updatedTriage: BulkTriageEntry, index: number) => {
    setTriages(update(index, updatedTriage, triages))
  }

  const handleAddTriage = () => setTriages([...triages, { reason: '' as TriageReasons }])

  const reasonGiven = triages[0].reason !== ''
  const needsNextFillDate = triages.some(
    entry => isRefillTooSoonReason(entry.selectedReason) && !entry.nextFillableDate,
  )
  const disableSave = !reasonGiven || needsNextFillDate

  const handleConfirm = () => {
    if (disableSave) return
    onConfirm(buildTriageParams(triages))
    dismissModal()
  }

  return (
    <ModalWrapper id="BulkTriageModal">
      <ModalHeader>
        <IconWrapper>
          <DangerIcon fill={bodyPrimaryColor} />
        </IconWrapper>
        <h2>Triage reason</h2>
      </ModalHeader>
      {triages.map((triageEntry, index) => (
        <TriageEntryFields
          key={index}
          index={index}
          value={triageEntry}
          triageReasons={triageReasons}
          onChange={handleTriageUpdate}
        />
      ))}
      <Box margin={{ top: '1rem' }} width={'15rem'}>
        <StyledActionButton
          data-testid="bulk-add-reason"
          icon={
            <IconWrapper>
              <PlusIcon fill="black" />
            </IconWrapper>
          }
          label="Add another triage reason"
          onClick={handleAddTriage}
        />
      </Box>
      <StyledButtonsContainer>
        <CancelButton label="Cancel" onClick={dismissModal} />
        <SaveButton disabled={disableSave} label="Confirm" isModal onClick={handleConfirm} />
      </StyledButtonsContainer>
    </ModalWrapper>
  )
}

const StyledButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  margin-top: 1.25rem;
`

const TriageIndexHeader = styled.h3`
  margin: 1.25rem 0 1.125rem;
`

const InputContainer = styled.div`
  width: 40rem;
  margin-top: 0.625rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  > textarea {
    padding-top: 0.3125rem;
    height: 7rem;
    margin-top: 0.625rem;
    border: 0.125rem solid ${borderColor};
  }
`

const StyledActionButton = styled(ActionButton)`
  margin-left: 0rem;
  background-color: ${primaryBackgroundColor};
`

const ReasonSelect = styled(StyledSelect)`
  height: 2.5rem;
`

export default BulkTriageModal
